import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Theme, useTheme } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useMemo } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../../lang/LangEnum';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { BreakpointSizes } from '../Hooks/useWidth';

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    '&>div': {
      flex: 1,
      minWidth: 0,
    },
  },
  navigationButton: {
    flexShrink: 0,
  },
  navigationButtonOutside: {
    [theme.breakpoints.up('lg')]: {
      position: 'absolute',
      zIndex: theme.zIndex.mobileStepper,
    },
  },
  navigationButtonOutsideRight: {
    [theme.breakpoints.up('lg')]: {
      right: theme.spacing(-4),
    },
  },
  navigationButtonOutsideLeft: {
    [theme.breakpoints.up('lg')]: {
      left: theme.spacing(-4),
    },
  },
  navigationButtonOverlay: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: theme.zIndex.mobileStepper,
  },
  navigationButtonOverlayRight: {
    right: theme.spacing(1),
  },
  navigationButtonOverlayLeft: {
    left: theme.spacing(1),
  },
  navigationButtonHidden: {
    visibility: 'hidden',
  },
}));

const buildMediaMatrix = <T,>(medias: T[], size: number) => {
  const matrix: T[][] = [[]];

  medias.forEach((media) => {
    if (matrix[matrix.length - 1].length === size) {
      matrix.push([]);
    }

    matrix[matrix.length - 1].push(media);
  });

  return matrix;
};

export const CAROUSEL_GRID_SIZES: BreakpointSizes = { xs: 12, sm: 4, md: 4, lg: 3, xl: 3 };

export const getItemsSizeBySelectedBreakpoint = (
  breakpontsSize: BreakpointSizes,
  selectedBreakpoint: Breakpoint,
) => {
  const currentColumns = breakpontsSize[selectedBreakpoint];
  const currentColumnsNumber =
    typeof currentColumns === 'number'
      ? currentColumns
      : parseInt(currentColumns ? currentColumns : '1', 10);
  return 12 / currentColumnsNumber;
};

type CarouselNavigation = 'outside' | 'overlay';

type GroupedCarouselProps<T> = {
  items: T[];
  renderItemGroup: (group: T[]) => React.ReactNode;
  groupSize: number;
  navigation?: CarouselNavigation;
  onChangeStep?: (step: number) => void;
};

const GroupedCarousel = <T,>({
  items,
  groupSize,
  navigation = 'outside',
  renderItemGroup,
  onChangeStep,
}: GroupedCarouselProps<T>) => {
  const { classes, cx } = useStyles();
  const { translate } = useTranslation();
  const [activeStep, setActiveStep] = React.useState(0);
  const itemsMatrix: T[][] = useMemo(
    () => buildMediaMatrix<T>(items, groupSize || 4),
    [groupSize, items],
  );
  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      if (onChangeStep) {
        onChangeStep(prevActiveStep + 1);
      }
      return prevActiveStep + 1;
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      if (onChangeStep) {
        onChangeStep(prevActiveStep - 1);
      }
      return prevActiveStep - 1;
    });
  };

  const handleStepChange = (step: number) => {
    if (onChangeStep) {
      onChangeStep(step);
    }
    setActiveStep(step);
  };

  const theme = useTheme();

  const isOverlayNavigation = navigation === 'overlay';
  const canGoBack = activeStep > 0;
  const canGoNext = activeStep < itemsMatrix.length - 1;

  const buildNavigationClassName = (side: 'left' | 'right', isEnabled: boolean) =>
    cx(classes.navigationButton, {
      [classes.navigationButtonOutside]: !isOverlayNavigation,
      [classes.navigationButtonOutsideLeft]: !isOverlayNavigation && side === 'left',
      [classes.navigationButtonOutsideRight]: !isOverlayNavigation && side === 'right',
      [classes.navigationButtonOverlay]: isOverlayNavigation,
      [classes.navigationButtonOverlayLeft]: isOverlayNavigation && side === 'left',
      [classes.navigationButtonOverlayRight]: isOverlayNavigation && side === 'right',
      [classes.navigationButtonHidden]: isOverlayNavigation && !isEnabled,
    });

  return (
    <div className={classes.container}>
      <IconButton
        icon={ChevronLeft}
        color={isOverlayNavigation ? 'secondary' : 'primary'}
        shape={isOverlayNavigation ? 'round' : undefined}
        size="large"
        disabled={!canGoBack}
        tabIndex={-1}
        ariaLabel={translate(LangEnum.GLOBAL_PREVIOUS)}
        className={buildNavigationClassName('left', canGoBack)}
        onClick={handleBack}
      />
      <SwipeableViews
        axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
        index={activeStep}
        onChangeIndex={handleStepChange}
        tabIndex={-1}
        enableMouseEvents
        ignoreNativeScroll
        slideStyle={{ boxSizing: 'border-box', overflow: 'hidden' }}
      >
        {itemsMatrix.map((itemGroup, index) => (
          <div key={index}>
            {Math.abs(activeStep - index) <= 2 ? renderItemGroup(itemGroup) : null}
          </div>
        ))}
      </SwipeableViews>
      <IconButton
        icon={ChevronRight}
        color={isOverlayNavigation ? 'secondary' : 'primary'}
        shape={isOverlayNavigation ? 'round' : undefined}
        size="large"
        disabled={!canGoNext}
        tabIndex={-1}
        ariaLabel={translate(LangEnum.GLOBAL_NEXT)}
        className={buildNavigationClassName('right', canGoNext)}
        onClick={handleNext}
      />
    </div>
  );
};

export default GroupedCarousel;
